import { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { Copy, Link2, Loader2, Search, TriangleAlert } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DatePicker } from '@/components/shared/DatePicker';
import { useCombinedPaymentsApi } from '@/services/api';
import { formatPLN } from '@/utils/currency';
import { cn } from '@/lib/utils';
import { t } from '@/i18n';
import type { CombinedPaymentCombination, CombinedPaymentSearchResult } from '@ip/shared-types';

interface MatchPaymentDialogProps {
  companyNip: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after a combination was confirmed — reload the list. */
  onLinked: () => void;
}

/** Clipboard text for the accounting system: one line per invoice + the sum. */
function comboToClipboard(combo: CombinedPaymentCombination): string {
  const lines = combo.invoices.map((invoice) =>
    [
      invoice.invoiceNumber ?? '—',
      invoice.vendorName ?? '—',
      invoice.saleDate ?? invoice.issueDate ?? '—',
      invoice.gross != null ? invoice.gross.toFixed(2) : '—',
    ].join('\t'),
  );
  lines.push(`${t('invoices.combinedPayment.sumLabel')}\t\t\t${combo.sum.toFixed(2)}`);
  return lines.join('\n');
}

/**
 * "Dopasuj płatność" (płatność łączona): the accountant types in the bank
 * transaction's date + amount; the backend proposes subsets of already-paid
 * PLN purchase invoices summing EXACTLY to that amount (the card charge
 * precedes the invoices, so they arrive paid). The proposed combination is
 * shown for verification (and copyable into the accounting system) before
 * confirming — confirmation only links the invoices to the transaction;
 * payment statuses are never changed.
 */
export function MatchPaymentDialog({
  companyNip,
  open,
  onOpenChange,
  onLinked,
}: MatchPaymentDialogProps) {
  const [transactionDate, setTransactionDate] = useState<Date | null>(new Date());
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [searching, setSearching] = useState(false);
  const [result, setResult] = useState<CombinedPaymentSearchResult | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [confirming, setConfirming] = useState(false);

  const { searchCombinations, createCombinedPayment } = useCombinedPaymentsApi();

  // Reset the form each time the dialog opens.
  useEffect(() => {
    if (open) {
      setTransactionDate(new Date());
      setAmount('');
      setNote('');
      setResult(null);
      setSelectedIndex(null);
    }
  }, [open]);

  const parsedAmount = amount === '' ? null : Number(amount);
  const amountInvalid = parsedAmount !== null && (!Number.isFinite(parsedAmount) || parsedAmount <= 0);
  const canSearch = !searching && transactionDate !== null && parsedAmount !== null && !amountInvalid;

  // Combined list: exact first, approximate (clearly marked) as fallback.
  const combinations: CombinedPaymentCombination[] = useMemo(
    () => (result ? (result.exact.length > 0 ? result.exact : result.approximate) : []),
    [result],
  );
  const isApproximate = result !== null && result.exact.length === 0 && result.approximate.length > 0;

  const handleSearch = useCallback(async () => {
    if (!transactionDate || parsedAmount === null || amountInvalid) return;
    try {
      setSearching(true);
      setSelectedIndex(null);
      const data = await searchCombinations(companyNip, {
        transactionDate: dayjs(transactionDate).format('YYYY-MM-DD'),
        amount: parsedAmount,
      });
      setResult(data);
      // Preselect the single exact match — one click less for the accountant.
      if (data.exact.length === 1) setSelectedIndex(0);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('invoices.combinedPayment.searchError'));
      setResult(null);
    } finally {
      setSearching(false);
    }
  }, [companyNip, transactionDate, parsedAmount, amountInvalid, searchCombinations]);

  const handleCopy = useCallback(async (combo: CombinedPaymentCombination) => {
    try {
      await navigator.clipboard.writeText(comboToClipboard(combo));
      toast.success(t('invoices.combinedPayment.copied'));
    } catch {
      toast.error(t('invoices.combinedPayment.copyError'));
    }
  }, []);

  const handleConfirm = useCallback(async () => {
    const combo = selectedIndex !== null ? combinations[selectedIndex] : undefined;
    if (!combo || !transactionDate || parsedAmount === null) return;
    // Only exact combinations can be confirmed — the backend re-verifies the
    // sum to the grosz; approximate proposals are for manual investigation.
    if (combo.difference !== 0) return;
    try {
      setConfirming(true);
      await createCombinedPayment(companyNip, {
        transactionDate: dayjs(transactionDate).format('YYYY-MM-DD'),
        amount: parsedAmount,
        invoiceIds: combo.invoices.map((invoice) => invoice.id),
        note: note.trim() || undefined,
      });
      toast.success(
        t('invoices.combinedPayment.acceptSuccess', {
          count: combo.invoices.length,
          amount: formatPLN(parsedAmount),
        }),
      );
      onOpenChange(false);
      onLinked();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('invoices.combinedPayment.acceptError'));
    } finally {
      setConfirming(false);
    }
  }, [selectedIndex, combinations, transactionDate, parsedAmount, note, companyNip, createCombinedPayment, onOpenChange, onLinked]);

  const selectedCombo = selectedIndex !== null ? combinations[selectedIndex] : undefined;
  const canConfirm = !confirming && selectedCombo !== undefined && selectedCombo.difference === 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('invoices.combinedPayment.dialogTitle')}</DialogTitle>
          <DialogDescription>{t('invoices.combinedPayment.dialogDescription')}</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-muted-foreground text-xs">
              {t('invoices.combinedPayment.transactionDate')}
            </Label>
            <DatePicker value={transactionDate} onChange={setTransactionDate} />
          </div>
          <div className="space-y-1">
            <Label className="text-muted-foreground text-xs">
              {t('invoices.combinedPayment.amount')}
            </Label>
            <Input
              type="number"
              step="0.01"
              min="0.01"
              placeholder="547.47"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && canSearch) handleSearch();
              }}
              aria-invalid={amountInvalid}
            />
          </div>
        </div>

        <Button onClick={handleSearch} disabled={!canSearch}>
          {searching ? <Loader2 className="size-4 animate-spin" /> : <Search className="size-4" />}
          {t('invoices.combinedPayment.searchButton')}
        </Button>

        {result && combinations.length === 0 && (
          <p className="text-muted-foreground text-sm">
            {t('invoices.combinedPayment.noMatches', {
              from: result.windowFrom ?? '—',
              to: result.windowTo ?? '—',
              count: result.candidateCount,
            })}
          </p>
        )}

        {combinations.length > 0 && (
          <div className="space-y-2">
            {isApproximate && (
              <p className="flex items-center gap-1.5 text-sm text-amber-600 dark:text-amber-400">
                <TriangleAlert className="size-4 shrink-0" />
                {t('invoices.combinedPayment.approxHeader')}
              </p>
            )}
            {combinations.map((combo, index) => {
              // Purchase confirm re-verifies the sum to the grosz — only an
              // exact combination (difference 0) can be linked. Approximate
              // proposals are shown for reference/copy but not selectable.
              const confirmable = combo.difference === 0;
              return (
              <div
                key={index}
                role="button"
                tabIndex={0}
                aria-disabled={!confirmable}
                onClick={() => confirmable && setSelectedIndex(index)}
                onKeyDown={(e) => {
                  if ((e.key === 'Enter' || e.key === ' ') && confirmable) setSelectedIndex(index);
                }}
                className={cn(
                  'rounded-md border p-3 transition-colors',
                  confirmable ? 'cursor-pointer' : 'opacity-70',
                  selectedIndex === index ? 'border-primary bg-primary/5' : confirmable && 'hover:bg-accent/50',
                )}
              >
                <div className="mb-2 flex items-center justify-between gap-2">
                  <span className="text-sm font-medium">
                    {t('invoices.combinedPayment.comboLabel', { count: combo.invoices.length })}
                  </span>
                  <div className="flex items-center gap-2">
                    {combo.difference !== 0 && (
                      <span className="flex items-center gap-1 text-xs font-medium text-destructive">
                        <TriangleAlert className="size-3.5" />
                        {t('invoices.combinedPayment.difference')}: {formatPLN(combo.difference)}
                      </span>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCopy(combo);
                      }}
                    >
                      <Copy className="size-3.5" />
                      {t('invoices.combinedPayment.copy')}
                    </Button>
                  </div>
                </div>
                <table className="w-full text-sm">
                  <tbody>
                    {combo.invoices.map((invoice) => (
                      <tr key={invoice.id} className="border-border/50 border-b last:border-0">
                        <td className="py-1 pr-2 font-mono text-xs">{invoice.invoiceNumber ?? '—'}</td>
                        <td className="text-muted-foreground max-w-48 truncate py-1 pr-2">
                          {invoice.vendorName ?? '—'}
                        </td>
                        <td className="py-1 pr-2 tabular-nums">{invoice.saleDate ?? invoice.issueDate ?? '—'}</td>
                        <td className="py-1 text-right tabular-nums">
                          {invoice.gross != null ? formatPLN(invoice.gross) : '—'}
                        </td>
                      </tr>
                    ))}
                    <tr>
                      <td colSpan={3} className="py-1 pr-2 text-right text-xs font-medium">
                        {t('invoices.combinedPayment.sumLabel')}
                      </td>
                      <td className="py-1 text-right font-semibold tabular-nums">{formatPLN(combo.sum)}</td>
                    </tr>
                  </tbody>
                </table>
                {!confirmable && (
                  <p className="text-muted-foreground mt-1 text-xs">
                    {t('invoices.combinedPayment.approxNotConfirmable')}
                  </p>
                )}
              </div>
              );
            })}
            {result?.truncated && (
              <p className="text-muted-foreground text-xs">{t('invoices.combinedPayment.truncated')}</p>
            )}
            <div className="space-y-1">
              <Label className="text-muted-foreground text-xs">
                {t('invoices.combinedPayment.noteLabel')}
              </Label>
              <Input
                placeholder={t('invoices.combinedPayment.notePlaceholder')}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                maxLength={1000}
              />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={confirming}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleConfirm} disabled={!canConfirm}>
            {confirming ? <Loader2 className="size-4 animate-spin" /> : <Link2 className="size-4" />}
            {t('invoices.combinedPayment.accept')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
